import {
  AutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import { Button, Grow, MenuItem, Typography } from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'

import TimePeriodForm from '../../components/TimePeriodForm'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { CHANNELS_QUERY } from '../../graphql/channel'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { TENANTS_QUERY } from '../../graphql/tenant'
import { DateObject } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { Accommodation } from '../../types/accommodation'
import { Channel } from '../../types/channel'
import { ComLogFilters, EntityTypeForComLog } from '../../types/monitoring'
import { RatePlanBrief } from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import { Tenant } from '../../types/tenants'
import useStyles from './styles/MonitoringFilters.styles'

type MonitoringFiltersProps = {
  filters: ComLogFilters
  setFilters: (v: ComLogFilters) => void
  setPage: (v: number) => void
  handleGetComlogs: () => void
}

export default function MonitoringFilters({
  filters,
  setFilters,
  setPage,
  handleGetComlogs
}: MonitoringFiltersProps) {
  const classes = useStyles()
  const [options, setoptions] = useState<
    { label: string; value: string; code: string }[]
  >([])
  const [accommodations, setaccommodations] = useState<Accommodation[]>([])
  const [tenants, settenants] = useState<Tenant[]>([])
  const [channels, setchannels] = useState<Channel[]>([])

  const isDisabled =
    !filters.To &&
    !filters.EndpointURL &&
    !filters.EndpointName &&
    !filters.EntityFilters[0]
  const EntityTypes: { value: EntityTypeForComLog; label: string }[] = [
    { value: 'Accommodation', label: getTranslate('Accommodation') },
    { value: 'Channel', label: getTranslate('Channel') },
    { value: 'Tenant', label: getTranslate('Tenant') },
    { value: 'RoomType', label: getTranslate('RoomType') },
    { value: 'RatePlan', label: getTranslate('RatePlan') }
  ]

  const datePickerRef = useRef<HTMLDivElement | null>(null)
  const [dateObject, setdateObject] = useState<DateObject>({})
  const [timePickerIsOpen, settimePickerIsOpen] = useState<boolean>(false)
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node) &&
        timePickerIsOpen
      ) {
        settimePickerIsOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, timePickerIsOpen])
  const [getAccommodations, { loading: loadingForAccommodations }] =
    useLazyQuery<{
      accommodations: Accommodation[]
    }>(ACCOMMODATIONS_QUERY, {
      onCompleted: (data) => {
        setaccommodations(data.accommodations)
      }
    })

  const [getTenants, { loading: loadingForTenant }] = useLazyQuery<{
    tenants: Tenant[]
  }>(TENANTS_QUERY, {
    onCompleted: (data) => {
      settenants(data.tenants)
    }
  })

  const [getChannels, { loading: loadingForChannel }] = useLazyQuery<{
    channels: Channel[]
  }>(CHANNELS_QUERY, {
    onCompleted: (data) => {
      setchannels(data.channels)
    }
  })
  const [getRoomTypes, { data: roomTypes, loading: loadingForRoomTypes }] =
    useLazyQuery<{
      roomTypes: RoomTypeBrief[]
    }>(ROOMTYPES_QUERY)
  const [getRatePlans, { data: rateplans, loading: loadingForRatePlans }] =
    useLazyQuery<{
      ratePlans: RatePlanBrief[]
    }>(RATEPLANS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    if (filters.EntityFilters[0]?.EntityType === 'Channel') {
      if (channels.length === 0) {
        getChannels()
      } else {
        setoptions(
          channels.map((channelItem) => ({
            label: channelItem.basicDetails.name,
            value: channelItem.basicDetails.channelID || '',
            code: channelItem.basicDetails.displayChannelCode
          }))
        )
      }
    } else if (
      filters.EntityFilters[0]?.EntityType === 'Accommodation' ||
      filters.EntityFilters[0]?.EntityType === 'RoomType' ||
      filters.EntityFilters[0]?.EntityType === 'RatePlan'
    ) {
      if (accommodations.length === 0) {
        getAccommodations()
      } else {
        setoptions(
          accommodations.map((accItem) => ({
            label: accItem.basicDetails.name,
            value: accItem.basicDetails.accommodationID || '',
            code: accItem.basicDetails.propertyCode
          }))
        )
      }
    } else if (filters.EntityFilters[0]?.EntityType === 'Tenant') {
      if (tenants.length === 0) {
        getTenants()
      } else {
        setoptions(
          tenants.map((tenantItem) => ({
            label: tenantItem.basicDetails.name,
            value: tenantItem.basicDetails?.tenantId || '',
            code: tenantItem.basicDetails?.tenantCode || ''
          }))
        )
      }
    }
  }, [filters.EntityFilters[0]?.EntityType, accommodations, tenants, channels])

  const getLabel = (entityId: string) => {
    if (filters.EntityFilters[0]?.EntityType === 'Channel') {
      return channels.find((ch) => ch.basicDetails.channelID === entityId)
        ?.basicDetails.name
    } else if (
      filters.EntityFilters[0]?.EntityType === 'Accommodation' ||
      filters.EntityFilters[0]?.EntityType === 'RoomType' ||
      filters.EntityFilters[0]?.EntityType === 'RatePlan'
    ) {
      return accommodations.find(
        (accommodation) =>
          accommodation.basicDetails.accommodationID === entityId
      )?.basicDetails.name
    } else if (filters.EntityFilters[0]?.EntityType === 'Tenant') {
      return tenants.find((tenant) => tenant.basicDetails.tenantId === entityId)
        ?.basicDetails.name
    }
  }
  const [autoCompleteLabelEntity, setautoCompleteLabelEntity] = useState<any>({
    label: getLabel(filters.EntityFilters[0]?.EntityID || ''),
    value: filters.EntityFilters[0]?.EntityID,
    code: filters.EntityFilters[0]?.EntityCode
  })
  const [autoCompleteLabelForRoomType, setautoCompleteLabelForRoomType] =
    useState<any>({
      label: roomTypes?.roomTypes.find(
        (item) => item.id === filters.EntityFilters[0]?.EntityID
      )?.name,
      value: filters.EntityFilters[0]?.EntityID,
      code: filters.EntityFilters[0]?.EntityCode
    })
  const [autoCompleteLabelForRateplan, setautoCompleteLabelForRateplan] =
    useState<any>({
      label: rateplans?.ratePlans.find(
        (item) => item.id === filters.EntityFilters[0]?.EntityID
      )?.name,
      value: filters.EntityFilters[0]?.EntityID,
      code: filters.EntityFilters[0]?.EntityCode
    })

  useEffect(() => {
    if (
      filters.EntityFilters[0]?.EntityType === 'Accommodation' ||
      filters.EntityFilters[0]?.EntityType === 'Channel' ||
      filters.EntityFilters[0]?.EntityType === 'Tenant'
    )
      setautoCompleteLabelEntity({
        label: getLabel(filters.EntityFilters[0]?.EntityID || ''),
        value: filters.EntityFilters[0]?.EntityID,
        code: filters.EntityFilters[0]?.EntityCode
      })
  }, [filters.EntityFilters[0]?.EntityID, tenants, channels, accommodations])

  const loading =
    loadingForAccommodations || loadingForTenant || loadingForChannel
  useEffect(() => {
    if (
      filters.EntityFilters[0]?.EntityType === 'RoomType' ||
      filters.EntityFilters[0]?.EntityType === 'RatePlan'
    ) {
      getRoomTypes({
        variables: {
          propertyCode: autoCompleteLabelEntity.code,
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: true
        }
      })
    }
  }, [autoCompleteLabelEntity])
  useEffect(() => {
    if (filters.EntityFilters[0]?.EntityType === 'RatePlan') {
      getRatePlans({
        variables: {
          propertyCode: autoCompleteLabelEntity.code,
          roomTypeCode: autoCompleteLabelForRoomType.code,
          showDeletedRatePlans: false,
          showDeactivedRatePlans: true
        }
      })
    }
  }, [autoCompleteLabelForRoomType])

  useEffect(() => {
    setFilters({
      ...filters,
      From: dateObject.gregorianStartDate || '',
      To: dateObject.gregorianEndDate || ''
    })
  }, [dateObject])

  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.selectBoxes}>
          <div className={classes.twoBoxInRow}>
            <InputWithLabel
              label={getTranslate('Endpoint URL')}
              fullWidth
              value={filters.EndpointURL}
              inputSize="small"
              onChange={(e) =>
                setFilters({ ...filters, EndpointURL: e.currentTarget.value })
              }
            />
            <InputWithLabel
              label={getTranslate('Endpoint Name')}
              fullWidth
              value={filters.EndpointName}
              onChange={(e) =>
                setFilters({ ...filters, EndpointName: e.currentTarget.value })
              }
              inputSize="small"
            />
          </div>
          <div className={classes.oneBoxInRow}>
            <SelectWithLabel
              noHelperText
              fullWidth
              value={
                filters.EntityFilters[0]?.EntityType
                  ? filters.EntityFilters[0]?.EntityType
                  : ''
              }
              label={getTranslate('Entity Type')}
              size="small"
              onChange={(e) => {
                setFilters({
                  ...filters,
                  EntityFilters: [
                    {
                      EntityType: e.target.value as EntityTypeForComLog,
                      EntityCode: '',
                      EntityID: ''
                    }
                  ]
                })
                setautoCompleteLabelForRoomType({
                  value: '',
                  label: '',
                  code: ''
                })
                setautoCompleteLabelForRateplan({
                  value: '',
                  label: '',
                  code: ''
                })
              }}>
              {EntityTypes.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </SelectWithLabel>
          </div>
          <Grow
            in={
              filters.EntityFilters.length > 0 &&
              !!filters.EntityFilters[0].EntityType
            }>
            <div className={classes.oneBoxInRow}>
              <AutocompleteWithLabel
                options={loading ? [] : options}
                size="small"
                getOptionLabel={(option) => option.label || ''}
                label={getTranslate(
                  filters.EntityFilters[0]?.EntityType === 'RatePlan' ||
                    filters.EntityFilters[0]?.EntityType === 'RoomType'
                    ? 'Accommodation'
                    : filters.EntityFilters[0]?.EntityType || ''
                )}
                value={autoCompleteLabelEntity}
                inputSize="small"
                onChange={(_, value: any) => {
                  if (
                    filters.EntityFilters[0]?.EntityType === 'Accommodation' ||
                    filters.EntityFilters[0]?.EntityType === 'Channel' ||
                    filters.EntityFilters[0]?.EntityType === 'Tenant'
                  ) {
                    setFilters({
                      ...filters,
                      EntityFilters: [
                        {
                          ...filters.EntityFilters[0],
                          EntityID: value?.value,
                          EntityCode: value?.code
                        }
                      ]
                    })
                  }

                  setautoCompleteLabelEntity({
                    value: value?.value,
                    label: value?.label,
                    code: value?.code
                  })
                  setautoCompleteLabelForRoomType({
                    value: '',
                    label: '',
                    code: ''
                  })
                  setautoCompleteLabelForRateplan({
                    value: '',
                    label: '',
                    code: ''
                  })
                }}
                loading={loading}
                loadingText={getTranslate('Loading') + '...'}
              />
            </div>
          </Grow>
          <Grow
            in={
              filters.EntityFilters.length > 0 &&
              (filters.EntityFilters[0].EntityType === 'RoomType' ||
                filters.EntityFilters[0].EntityType === 'RatePlan')
            }>
            <div className={classes.twoBoxInRow}>
              <AutocompleteWithLabel
                disabled={!!!autoCompleteLabelEntity.value}
                options={
                  roomTypes?.roomTypes.map((item) => ({
                    label: item.name,
                    value: item.id || '',
                    code: item.displayRoomTypeCode
                  })) || []
                }
                fullWidth
                size="small"
                getOptionLabel={(option) => option.label || ''}
                label={getTranslate('RoomType')}
                value={autoCompleteLabelForRoomType}
                inputSize="small"
                onChange={(_, value: any) => {
                  if (filters.EntityFilters[0]?.EntityType === 'RoomType') {
                  }
                  setFilters({
                    ...filters,
                    EntityFilters: [
                      {
                        ...filters.EntityFilters[0],
                        EntityID: value?.value,
                        EntityCode: value?.code
                      }
                    ]
                  })
                  setautoCompleteLabelForRoomType({
                    value: value?.value,
                    label: value?.label,
                    code: value?.code
                  })
                }}
                loading={loadingForRoomTypes}
                loadingText={getTranslate('Loading') + '...'}
              />

              {filters.EntityFilters.length > 0 &&
                filters.EntityFilters[0].EntityType === 'RatePlan' && (
                  <AutocompleteWithLabel
                    disabled={!!!autoCompleteLabelForRoomType.value}
                    options={
                      rateplans?.ratePlans.map((item) => ({
                        label: item.name,
                        value: item.id || '',
                        code: item.displayRatePlanCode
                      })) || []
                    }
                    fullWidth
                    size="small"
                    getOptionLabel={(option) => option.label || ''}
                    label={getTranslate(
                      filters.EntityFilters[0]?.EntityType || ''
                    )}
                    value={autoCompleteLabelForRateplan}
                    inputSize="small"
                    onChange={(_, value: any) => {
                      if (filters.EntityFilters[0]?.EntityType === 'RatePlan') {
                      }
                      setFilters({
                        ...filters,
                        EntityFilters: [
                          {
                            ...filters.EntityFilters[0],
                            EntityID: value?.value,
                            EntityCode: value?.code
                          }
                        ]
                      })
                      setautoCompleteLabelForRateplan({
                        value: value?.value,
                        label: value?.label,
                        code: value?.code
                      })
                    }}
                    loading={loadingForRatePlans}
                    loadingText={getTranslate('Loading') + '...'}
                  />
                )}
            </div>
          </Grow>
        </div>
        <div ref={datePickerRef} className={classes.datePickerContainer}>
          <Typography variant="body1">{getTranslate('Time Period')}</Typography>
          <TimePeriodForm
            isOpen={timePickerIsOpen}
            setTimePickerSelected={settimePickerIsOpen}
            dateObject={dateObject}
            onChange={(data: DateObject) => {
              setdateObject(data)
            }}
            onDoneClickForCloseDatePicker={timePickerIsOpen}
            setOnDoneClickForCloseDatePicker={settimePickerIsOpen}
            enablePastDays
          />
          <div className={classes.ButtonsContainer}>
            <Button
              variant="text"
              color="secondary"
              onClick={() => {
                setFilters({
                  EndpointName: '',
                  EndpointURL: '',
                  EntityFilters: [],
                  From: '',
                  page: 1,
                  perPage: 8,
                  To: ''
                })
                setdateObject({})
              }}>
              <Typography className={classes.resetText}>
                {getTranslate('Reset')}
              </Typography>
            </Button>
            <Button
              disabled={isDisabled}
              variant="contained"
              color="primary"
              onClick={() => {
                setPage(1)
                handleGetComlogs()
              }}>
              <Typography className={classes.applyText}>
                {getTranslate('Apply')}
              </Typography>
            </Button>
          </div>
        </div>
      </div>
    </Grow>
  )
}
